import * as PIXI from 'pixi.js';
import { globalEventBus, GameEvents } from '../core/EventBus.js';

/**
 * 拼图块类
 * 表示单个可拖拽、可旋转的拼图块
 */
export class Piece {
    constructor(options) {
        const {
            app,
            col,
            row,
            size,
            padding,
            path,
            texture = null,
            color = null,
            pieceWidth,
            pieceHeight,
            totalCols = 5,
            totalRows = 4,
            bevelConfig = null
        } = options;

        this.app = app;

        // 存储总行列数（用于图片渲染）
        this.totalCols = totalCols;
        this.totalRows = totalRows;

        // 网格位置
        this.col = col;
        this.row = row;

        // 尺寸信息
        this.size = size;
        this.padding = padding;
        this.pieceWidth = pieceWidth || size;
        this.pieceHeight = pieceHeight || size;

        // 路径数据
        this.pathData = path;

        // 浮雕配置
        this.bevelConfig = bevelConfig;

        // 状态
        this.isSnapped = false;         // 是否已吸附到正确位置
        this.isRotating = false;        // 是否正在旋转动画中
        this.rotation = 0;              // 当前旋转角度（度）
        this.correctRotation = 0;       // 正确的旋转角度

        // PIXI容器
        this.container = new PIXI.Container();
        this.container.eventMode = 'static';
        this.container.cursor = 'grab';
        this.container.cullable = true; // 开启剔除优化，屏幕外的碎片不渲染

        // 存储对piece的引用，方便事件处理
        this.container.piece = this;

        // 创建图形
        this.graphics = null;
        this.sprite = null;
        this.mask = null;

        // 正确位置（用于吸附检测）
        this.correctX = 0;
        this.correctY = 0;

        this.baseShadowOffset = bevelConfig?.shadowOffset || 3; // 基础阴影偏移
        this.liftShadowOffset = this.baseShadowOffset + 20;    // 悬浮时的阴影偏移 (大幅增加高度感)
        
        // 事件总线
        this.events = globalEventBus;

        // 根据是否有纹理决定渲染方式
        if (texture) {
            this.createWithTexture(texture, color);
        } else {
            this.createWithColor(color);
        }
    }

    /**
     * 提起碎片（悬浮效果）
     * 仅处理阴影和状态标记，实际缩放由 DragController 的临时容器处理
     */
    lift() {
        if (this.isLifted) return;
        this.isLifted = true;
        
        // 显示高亮边框
        if (this.selectionStroke) {
            this.selectionStroke.visible = true;
        }

        // 目标：阴影偏移增加 (模拟离地更远)
        if (this.shadowLayer) {
            // 注意：shadowLayer 可能是 Sprite (有 bounds 偏移) 或 Graphics
            // 如果是 Sprite，我们需要在原有偏移基础上增加 liftShadowOffset
            // 但为了简化，我们假设 shadowLayer.x/y 初始包含了 localBounds.x/y
            // 这里我们使用相对移动会更安全，或者记录初始位置。
            // 简单起见，我们假设 shadowLayer 是 Sprite，它的 pivot/anchor 默认是 (0,0)
            // 而它的位置被设置为了 bounds.x, bounds.y
            // 我们需要给它增加一个全局偏移。
            
            // 更稳妥的方式：直接修改 transform
            // 但因为 createWithTexture 中我们设置了 this.shadowLayer.x = bounds.x
            // 这里如果直接覆盖 x，会导致错位。
            
            // 解决方案：使用 transform 的相对移动比较麻烦。
            // 让我们在 createWithTexture 中把 Sprite 放入一个容器？或者直接修改 Sprite 位置？
            // 实际上，我们在 createWithTexture 中： this.shadowLayer.x = bounds.x;
            // 所以这里应该是： this.shadowLayer.x = bounds.x + offset;
            // 但是 bounds.x 没存下来。
            
            // 简单修正：记录初始位置
            if (this._shadowBaseX === undefined) this._shadowBaseX = this.shadowLayer.x;
            if (this._shadowBaseY === undefined) this._shadowBaseY = this.shadowLayer.y;

            this.shadowLayer.x = this._shadowBaseX + this.liftShadowOffset;
            this.shadowLayer.y = this._shadowBaseY + this.liftShadowOffset;
            
            // 模拟扩散：放大 Sprite
            // 预烘焙的模糊已经有一定 blur，放大后会显得更模糊
            this.shadowLayer.scale.set(1.15); 
            this.shadowLayer.alpha = 0.3; 
        }
    }

    /**
     * 放下碎片
     */
    drop() {
        if (!this.isLifted) return;
        this.isLifted = false;

        // 隐藏高亮边框
        if (this.selectionStroke) {
            this.selectionStroke.visible = false;
        }

        // 恢复原始阴影
        if (this.shadowLayer) {
            if (this._shadowBaseX !== undefined) {
                this.shadowLayer.x = this._shadowBaseX + this.baseShadowOffset;
                this.shadowLayer.y = this._shadowBaseY + this.baseShadowOffset;
            } else {
                // Fallback if lift() wasn't called yet or graphics mode
                this.shadowLayer.x = this.baseShadowOffset;
                this.shadowLayer.y = this.baseShadowOffset;
            }
            
            this.shadowLayer.scale.set(1.0); 
            this.shadowLayer.alpha = 0.5;
        }
    }

    /**
     * 根据SVG路径生成多边形点击区域
     * @param {string} pathStr SVG路径字符串
     * @returns {PIXI.Polygon} 多边形区域
     */
    createPolygonHitArea(pathStr) {
        const commands = this.parseSVGPath(pathStr);
        const points = [];
        let startX = 0, startY = 0;
        let lastX = 0, lastY = 0;

        // 贝塞尔曲线采样精度 (每段曲线采样的点数)
        // 5-8 个点对于拼图来说足够平滑且性能开销低
        const segments = 6; 

        for (const cmd of commands) {
            if (cmd.type === 'M') {
                startX = cmd.x;
                startY = cmd.y;
                points.push(new PIXI.Point(cmd.x, cmd.y));
                lastX = cmd.x;
                lastY = cmd.y;
            } else if (cmd.type === 'L') {
                points.push(new PIXI.Point(cmd.x, cmd.y));
                lastX = cmd.x;
                lastY = cmd.y;
            } else if (cmd.type === 'C') {
                // 三次贝塞尔曲线采样
                // B(t) = (1-t)^3*P0 + 3(1-t)^2*t*P1 + 3(1-t)*t^2*P2 + t^3*P3
                for (let i = 1; i <= segments; i++) {
                    const t = i / segments;
                    const t1 = 1 - t;
                    
                    const a = t1 * t1 * t1;
                    const b = 3 * t1 * t1 * t;
                    const c = 3 * t1 * t * t;
                    const d = t * t * t;

                    const x = a * lastX + b * cmd.x1 + c * cmd.x2 + d * cmd.x;
                    const y = a * lastY + b * cmd.y1 + c * cmd.y2 + d * cmd.y;
                    
                    points.push(new PIXI.Point(x, y));
                }
                lastX = cmd.x;
                lastY = cmd.y;
            } else if (cmd.type === 'Z') {
                // 闭合
                // points.push(new PIXI.Point(startX, startY));
            }
        }

        return new PIXI.Polygon(points);
    }

    /**
     * 使用纹理创建拼图块
     * @param {PIXI.Texture} texture 完整图片纹理
     * @param {number} tintColor 可选的着色
     */
    createWithTexture(texture, tintColor = null) {
        const svgSize = this.size + this.padding * 2;

        // 计算纹理尺寸
        const texWidth = texture.width;
        const texHeight = texture.height;

        // 计算拼图总尺寸
        const puzzleWidth = this.size * this.totalCols;
        const puzzleHeight = this.size * this.totalRows;

        // 获取浮雕质量等级
        const bevelQuality = this.bevelConfig?.quality || 'none';

        // === 1. 阴影层（保持独立，因为需要动态隐藏） ===
        if (bevelQuality === 'high' || bevelQuality === 'low') {
            // 性能优化：预烘焙阴影
            // 不直接使用 Graphics + Filter (昂贵)，而是生成一张静态模糊纹理 (廉价)
            const shadowGraph = new PIXI.Graphics();
            this.drawFilledPath(shadowGraph, 0x000000);
            
            // 使用临时滤镜生成模糊效果
            const blurFilter = new PIXI.BlurFilter();
            blurFilter.blur = 4; // 基础模糊度 (预烘焙中等模糊)
            blurFilter.quality = 2;
            shadowGraph.filters = [blurFilter];

            // 增加 padding 防止模糊被裁剪
            const bounds = shadowGraph.getLocalBounds();
            const padding = 10;
            bounds.pad(padding);

            try {
                const shadowTexture = this.app.renderer.generateTexture({
                    target: shadowGraph,
                    resolution: 1, // 阴影不需要过高分辨率
                    scaleMode: 'linear',
                    region: bounds
                });

                this.shadowLayer = new PIXI.Sprite(shadowTexture);
                // 校正位置：纹理生成的 Sprite 锚点在左上角，需要补偿 bounds 的偏移
                this.shadowLayer.x = bounds.x;
                this.shadowLayer.y = bounds.y;
                
                this.shadowLayer.alpha = 0.5;
                this.container.addChild(this.shadowLayer);
            } catch (e) {
                console.warn("Shadow bake failed, fallback to simple graphics", e);
                // 降级方案：无模糊的简单阴影
                shadowGraph.filters = null;
                this.shadowLayer = shadowGraph;
                this.shadowLayer.alpha = 0.3;
                this.container.addChild(this.shadowLayer);
            }
            
            // 如果成功生成了 Texture，原来的 Graphics 就可以销毁了
            if (this.shadowLayer !== shadowGraph) {
                shadowGraph.destroy();
            }
        }

        // === 2. 烘焙内容层（图片 + 遮罩 + 边框 + 浮雕） ===
        // 创建一个临时容器用于构建外观
        const bakeContainer = new PIXI.Container();

        // --- 图片与遮罩 ---
        const sprite = new PIXI.Sprite(texture);
        
        // 使用等比缩放 (Cover 模式)，避免图片拉伸变形
        // 计算缩放比例，确保图片能覆盖整个拼图区域
        const scaleX = puzzleWidth / texWidth;
        const scaleY = puzzleHeight / texHeight;
        const scale = Math.max(scaleX, scaleY);
        
        sprite.scale.set(scale);

        // 计算居中偏移
        // 图片缩放后的尺寸
        const scaledTexWidth = texWidth * scale;
        const scaledTexHeight = texHeight * scale;

        // 计算图片相对于拼图区域的居中偏移量 (通常为负或0)
        const offsetX = (puzzleWidth - scaledTexWidth) / 2;
        const offsetY = (puzzleHeight - scaledTexHeight) / 2;

        // 设置 Sprite 位置
        // 1. padding: 碎片内部的内边距
        // 2. -col * size: 移到当前碎片对应的网格位置
        // 3. offsetX/Y: 加上整体图片的居中偏移
        sprite.x = this.padding - this.col * this.size + offsetX;
        sprite.y = this.padding - this.row * this.size + offsetY;

        const mask = new PIXI.Graphics();
        this.drawFilledPath(mask, 0xffffff);
        sprite.mask = mask;

        bakeContainer.addChild(sprite);
        bakeContainer.addChild(mask);

        // --- 高光与暗边 ---
        if (bevelQuality === 'high') {
            const highlightLayer = new PIXI.Graphics();
            this.drawBevelEdge(highlightLayer, 'highlight');
            highlightLayer.alpha = this.bevelConfig?.highlightAlpha || 0.4;
            bakeContainer.addChild(highlightLayer);

            const darkEdgeLayer = new PIXI.Graphics();
            this.drawBevelEdge(darkEdgeLayer, 'shadow');
            darkEdgeLayer.alpha = this.bevelConfig?.darkEdgeAlpha || 0.2;
            bakeContainer.addChild(darkEdgeLayer);
        }

        // --- 边框 ---
        const borderLayer = new PIXI.Graphics();
        this.drawStrokePath(borderLayer, 0x000000, 1);
        borderLayer.alpha = 0.3;
        bakeContainer.addChild(borderLayer);

        // === 3. 生成纹理并替换 ===
        // 最终决定：为了保证旋转轴心的绝对准确，放弃手动 generateTexture。
        // 直接使用 bakeContainer (容器 + 遮罩) 避免了一切纹理坐标系转换带来的偏差。
        // 虽然理论上会增加 draw calls，但对于常规拼图规模，这是保证体验的唯一稳妥方案。
        this.sprite = bakeContainer;
        
        // 移除 generateTexture 相关代码，防止再次引入坐标 Bug
        // if (this.app && this.app.renderer) { ... }
        
        this.container.addChild(this.sprite);

        // 设置容器点击区域 (HitArea)
        const hitPoly = this.createPolygonHitArea(this.pathData);
        this.container.hitArea = hitPoly;

        // --- 1. 基于多边形顶点计算精确的几何边界和中心 ---
        let minX = Infinity, maxX = -Infinity;
        let minY = Infinity, maxY = -Infinity;
        
        const points = hitPoly.points;
        const isFlat = typeof points[0] === 'number';
        
        for (let i = 0; i < points.length; i++) {
            let x, y;
            if (isFlat) {
                x = points[i];
                y = points[i+1];
                i++;
            } else {
                x = points[i].x;
                y = points[i].y;
            }
            
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
        }

        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        // --- 2. 生成纹理 (恢复高性能方案) ---
        // 使用计算出的几何边界作为烘焙区域，确保纹理坐标系与 Pivot 完全对齐
        if (this.app && this.app.renderer) {
            try {
                // 修正：不再手动计算 region，而是直接询问容器的精确边界
                // 这能自动包含描边(bevel/border)的宽度，避免位置计算误差
                const region = bakeContainer.getLocalBounds();

                // 稍微扩展一点 region 以防抗锯齿被裁剪 (可选，getLocalBounds通常够用)
                // region.pad(2); 

                const bakedTexture = this.app.renderer.generateTexture({
                    target: bakeContainer,
                    resolution: this.app.renderer.resolution,
                    scaleMode: 'linear',
                    antialias: true,
                    region: region
                });

                this.sprite = new PIXI.Sprite(bakedTexture);
                
                // 将 Sprite 放置在 Region 的起始位置
                // 这样无论 region 算出来是啥，sprite 都会严丝合缝地回到原来的位置
                this.sprite.x = region.x;
                this.sprite.y = region.y;

            } catch (e) {
                console.error("Failed to generate texture, fallback to dynamic rendering", e);
                this.sprite = bakeContainer; 
            }
        } else {
            this.sprite = bakeContainer;
        }
        
        // 销毁临时对象
        if (this.sprite !== bakeContainer) {
            bakeContainer.destroy({ children: true });
        }

        this.container.addChild(this.sprite);

        // --- 选中高亮边框 (Selection Stroke) ---
        // 预先创建，平时隐藏，lift时显示
        this.selectionStroke = new PIXI.Graphics();
        // 改为内描边 (alignment: 0)，让线框压在图片内部，消除视觉缝隙
        this.drawStrokePath(this.selectionStroke, 0xFFD700, 1, 0); 
        this.selectionStroke.visible = false;
        // 移除手动偏移
        // 添加到顶层
        this.container.addChild(this.selectionStroke);

        // --- 3. 设置 Pivot ---
        // Pivot 设置在几何中心
        this.container.pivot.set(centerX, centerY);
        
        // 修正坐标
        this.container.x += centerX;
        this.container.y += centerY;
    }

    /**
     * 使用纯色创建拼图块
     * @param {number} color 颜色值
     */
    createWithColor(color) {
        const svgSize = this.size + this.padding * 2;

        // 计算颜色（如果未指定则根据位置生成）
        const fillColor = color !== null ? color : this.generateColor();

        // 创建图形
        this.graphics = new PIXI.Graphics();
        this.drawPath(this.graphics, true, fillColor);

        // 创建标签
        this.label = new PIXI.Text({
            text: `${this.col},${this.row}`,
            style: {
                fontSize: 11,
                fontWeight: 'bold',
                fill: 0x222222
            }
        });
        this.label.anchor.set(0.5);
        this.label.x = this.padding + this.size / 2;
        this.label.y = this.padding + this.size / 2;

        // 添加到容器
        this.container.addChild(this.graphics);
        this.container.addChild(this.label);

        // 设置容器大小 (HitArea)
        // 使用精准多边形判定
        const hitPoly = this.createPolygonHitArea(this.pathData);
        this.container.hitArea = hitPoly;

        // --- 关键优化：使用多边形顶点计算真实的视觉中心 ---
        let minX = Infinity, maxX = -Infinity;
        let minY = Infinity, maxY = -Infinity;
        
        const points = hitPoly.points;
        const isFlat = typeof points[0] === 'number';
        
        for (let i = 0; i < points.length; i++) {
            let x, y;
            if (isFlat) {
                x = points[i];
                y = points[i+1];
                i++;
            } else {
                x = points[i].x;
                y = points[i].y;
            }
            
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
        }

        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        // --- 选中高亮边框 (Selection Stroke) ---
        this.selectionStroke = new PIXI.Graphics();
        this.drawStrokePath(this.selectionStroke, 0xFFD700, 1, 0); // 内描边
        this.selectionStroke.visible = false;
        this.container.addChild(this.selectionStroke);

        this.container.pivot.set(centerX, centerY);
        this.container.x += centerX;
        this.container.y += centerY;
    }

    /**
     * 绘制浮雕边缘效果
     * @param {PIXI.Graphics} graphics 图形对象
     * @param {string} type 'highlight' 或 'shadow'
     */
    drawBevelEdge(graphics, type) {
        const commands = this.parseSVGPath(this.pathData);
        const isHighlight = type === 'highlight';
        const color = isHighlight
            ? (this.bevelConfig?.highlightColor || 0xffffff)
            : (this.bevelConfig?.darkEdgeColor || 0x000000);

        // 计算每个线段的方向，决定是否绘制
        // 高光：左侧和上侧边缘（法向量指向左上）
        // 暗边：右侧和下侧边缘（法向量指向右下）

        let lastX = 0, lastY = 0;
        let firstX = 0, firstY = 0;

        graphics.beginPath();

        for (let i = 0; i < commands.length; i++) {
            const cmd = commands[i];

            if (cmd.type === 'M') {
                lastX = cmd.x;
                lastY = cmd.y;
                firstX = cmd.x;
                firstY = cmd.y;
                graphics.moveTo(cmd.x, cmd.y);
            } else if (cmd.type === 'L') {
                // 计算线段方向
                const dx = cmd.x - lastX;
                const dy = cmd.y - lastY;

                // 法向量（垂直于线段，指向左侧）
                const nx = -dy;
                const ny = dx;

                // 判断是否应该绘制这条边
                // 高光：法向量指向左上（nx < 0 或 ny < 0）
                // 暗边：法向量指向右下（nx > 0 或 ny > 0）
                const shouldDraw = isHighlight
                    ? (nx < 0 || ny < 0)
                    : (nx > 0 || ny > 0);

                if (shouldDraw) {
                    graphics.lineTo(cmd.x, cmd.y);
                } else {
                    graphics.moveTo(cmd.x, cmd.y);
                }

                lastX = cmd.x;
                lastY = cmd.y;
            } else if (cmd.type === 'C') {
                // 对于贝塞尔曲线，使用起点和终点的方向判断
                const dx = cmd.x - lastX;
                const dy = cmd.y - lastY;
                const nx = -dy;
                const ny = dx;

                const shouldDraw = isHighlight
                    ? (nx < 0 || ny < 0)
                    : (nx > 0 || ny > 0);

                if (shouldDraw) {
                    graphics.bezierCurveTo(cmd.x1, cmd.y1, cmd.x2, cmd.y2, cmd.x, cmd.y);
                } else {
                    graphics.moveTo(cmd.x, cmd.y);
                }

                lastX = cmd.x;
                lastY = cmd.y;
            } else if (cmd.type === 'Z') {
                // 闭合路径时检查最后一条边
                const dx = firstX - lastX;
                const dy = firstY - lastY;
                const nx = -dy;
                const ny = dx;

                const shouldDraw = isHighlight
                    ? (nx < 0 || ny < 0)
                    : (nx > 0 || ny > 0);

                if (shouldDraw) {
                    graphics.lineTo(firstX, firstY);
                }
            }
        }

        graphics.stroke({ color, width: 2 });
    }

    /**
     * 绘制填充路径
     * @param {PIXI.Graphics} graphics 图形对象
     * @param {number} color 填充颜色
     */
    drawFilledPath(graphics, color) {
        const commands = this.parseSVGPath(this.pathData);

        graphics.beginPath();
        for (const cmd of commands) {
            switch (cmd.type) {
                case 'M':
                    graphics.moveTo(cmd.x, cmd.y);
                    break;
                case 'L':
                    graphics.lineTo(cmd.x, cmd.y);
                    break;
                case 'C':
                    graphics.bezierCurveTo(cmd.x1, cmd.y1, cmd.x2, cmd.y2, cmd.x, cmd.y);
                    break;
                case 'Z':
                    graphics.closePath();
                    break;
            }
        }
        graphics.fill({ color });
    }

    /**
     * 绘制描边路径
     * @param {PIXI.Graphics} graphics 图形对象
     * @param {number} color 描边颜色
     * @param {number} width 描边宽度
     * @param {number} alignment 描边对齐 (0=内, 0.5=中, 1=外)
     */
    drawStrokePath(graphics, color, width, alignment = 0.5) {
        const commands = this.parseSVGPath(this.pathData);

        graphics.beginPath();
        for (const cmd of commands) {
            switch (cmd.type) {
                case 'M':
                    graphics.moveTo(cmd.x, cmd.y);
                    break;
                case 'L':
                    graphics.lineTo(cmd.x, cmd.y);
                    break;
                case 'C':
                    graphics.bezierCurveTo(cmd.x1, cmd.y1, cmd.x2, cmd.y2, cmd.x, cmd.y);
                    break;
                case 'Z':
                    graphics.closePath();
                    break;
            }
        }
        graphics.stroke({ color, width, alignment });
    }

    /**
     * 绘制拼图块路径（填充+描边）
     * @param {PIXI.Graphics} graphics 图形对象
     * @param {boolean} fill 是否填充
     * @param {number} color 颜色
     * @param {number} strokeWidth 描边宽度
     */
    drawPath(graphics, fill = true, color = 0xffffff, strokeWidth = 1.5) {
        if (fill) {
            this.drawFilledPath(graphics, color);
        }
        this.drawStrokePath(graphics, 0x222222, strokeWidth);
    }

    /**
     * 解析SVG路径字符串
     * @param {string} pathStr 路径字符串
     * @returns {Array} 命令数组
     */
    parseSVGPath(pathStr) {
        const commands = [];
        const regex = /([MLCZ])\s*([^MLCZ]*)/gi;
        let match;

        while ((match = regex.exec(pathStr)) !== null) {
            const type = match[1].toUpperCase();
            const args = match[2].trim().split(/[\s,]+/).filter(s => s).map(Number);

            switch (type) {
                case 'M':
                case 'L':
                    commands.push({ type, x: args[0], y: args[1] });
                    break;
                case 'C':
                    commands.push({
                        type,
                        x1: args[0], y1: args[1],
                        x2: args[2], y2: args[3],
                        x: args[4], y: args[5]
                    });
                    break;
                case 'Z':
                    commands.push({ type });
                    break;
            }
        }

        return commands;
    }

    /**
     * 根据位置生成颜色
     * @returns {number} 颜色值
     */
    generateColor() {
        const hue = ((this.col * 67 + this.row * 131) % 360);
        return this.hslToHex(hue, 65, 55);
    }

    /**
     * HSL转Hex颜色
     * @param {number} h 色相 0-360
     * @param {number} s 饱和度 0-100
     * @param {number} l 亮度 0-100
     * @returns {number} Hex颜色值
     */
    hslToHex(h, s, l) {
        s /= 100;
        l /= 100;

        const c = (1 - Math.abs(2 * l - 1)) * s;
        const x = c * (1 - Math.abs((h / 60) % 2 - 1));
        const m = l - c / 2;

        let r = 0, g = 0, b = 0;

        if (h < 60) { r = c; g = x; }
        else if (h < 120) { r = x; g = c; }
        else if (h < 180) { g = c; b = x; }
        else if (h < 240) { g = x; b = c; }
        else if (h < 300) { r = x; b = c; }
        else { r = c; b = x; }

        r = Math.round((r + m) * 255);
        g = Math.round((g + m) * 255);
        b = Math.round((b + m) * 255);

        return (r << 16) + (g << 8) + b;
    }

    /**
     * 设置正确位置
     * @param {number} x X坐标
     * @param {number} y Y坐标
     */
    setCorrectPosition(x, y) {
        this.correctX = x;
        this.correctY = y;
    }

    /**
     * 设置位置（基于左上角，世界坐标系）
     * @param {number} x X坐标
     * @param {number} y Y坐标
     */
    setPosition(x, y) {
        const pivotX = this.container.pivot.x;
        const pivotY = this.container.pivot.y;

        // 如果没有旋转，直接加上 pivot
        if (this.container.rotation === 0) {
            this.container.x = x + pivotX;
            this.container.y = y + pivotY;
            return;
        }

        // 如果有旋转，加上旋转后的 pivot 向量
        const rot = this.container.rotation;
        const cos = Math.cos(rot);
        const sin = Math.sin(rot);

        const rotPivotX = pivotX * cos - pivotY * sin;
        const rotPivotY = pivotX * sin + pivotY * cos;

        this.container.x = x + rotPivotX;
        this.container.y = y + rotPivotY;
    }

    /**
     * 获取位置（基于左上角，世界坐标系）
     * 考虑了旋转对 Pivot 偏移的影响
     * @returns {Object} 位置对象
     */
    getPosition() {
        const pivotX = this.container.pivot.x;
        const pivotY = this.container.pivot.y;
        
        // 如果没有旋转，直接减去 pivot
        if (this.container.rotation === 0) {
            return {
                x: this.container.x - pivotX,
                y: this.container.y - pivotY
            };
        }

        // 如果有旋转，需要将 pivot 向量旋转后再减去
        // Pos = Center - Rotate(Pivot)
        const rot = this.container.rotation; // 弧度
        const cos = Math.cos(rot);
        const sin = Math.sin(rot);

        // 旋转后的 Pivot 向量
        const rotPivotX = pivotX * cos - pivotY * sin;
        const rotPivotY = pivotX * sin + pivotY * cos;

        return {
            x: this.container.x - rotPivotX,
            y: this.container.y - rotPivotY
        };
    }

    /**
     * 设置旋转角度
     * @param {number} degrees 角度
     */
    setRotation(degrees) {
        this.rotation = degrees % 360;
        if (this.rotation < 0) this.rotation += 360;

        const svgSize = this.size + this.padding * 2;
        const centerOffset = svgSize / 2;

        // 确保pivot设置到中心
        if (this.container.pivot.x === 0 && this.container.pivot.y === 0 && degrees !== 0) {
            this.container.pivot.set(centerOffset, centerOffset);
            this.container.x += centerOffset;
            this.container.y += centerOffset;
        }

        this.container.rotation = this.rotation * Math.PI / 180;
    }

    /**
     * 检查旋转是否正确
     * @returns {boolean} 是否正确
     */
    isRotationCorrect() {
        const diff = Math.abs(this.rotation - this.correctRotation);
        return diff < 5 || diff > 355; // 允许5度误差
    }

    /**
     * 吸附到正确位置
     */
    snap() {
        this.isSnapped = true;
        this.setPosition(this.correctX, this.correctY);
        this.setRotation(this.correctRotation);
        
        // 修改：吸附后不再锁定交互，允许玩家再次拖动
        // this.container.cursor = 'default';
        // this.container.eventMode = 'none';

        this.events.emit(GameEvents.PIECE_SNAP, this);
    }

    /**
     * 解除吸附状态（当玩家再次拖动已吸附的拼图时调用）
     */
    releaseSnap() {
        if (this.isSnapped) {
            this.isSnapped = false;
            this.container.cursor = 'grab';
            this.container.eventMode = 'static';
        }
    }

    /**
     * 检查是否在正确位置附近
     * @param {number} threshold 距离阈值
     * @returns {boolean} 是否接近正确位置
     */
    isNearCorrectPosition(threshold = 15) {
        if (this.isSnapped) return false;

        // 使用 getPosition() 来获取考虑了 pivot 偏移的逻辑位置
        const pos = this.getPosition();
        const dx = Math.abs(pos.x - this.correctX);
        const dy = Math.abs(pos.y - this.correctY);

        return dx < threshold && dy < threshold && this.isRotationCorrect();
    }

    /**
     * 高亮显示
     * @param {boolean} highlight 是否高亮
     */
    setHighlight(highlight) {
        if (highlight) {
            this.container.alpha = 0.8;
        } else {
            this.container.alpha = 1;
        }
    }

    /**
     * 带入层级
     */
    bringToFront() {
        const parent = this.container.parent;
        if (parent) {
            parent.removeChild(this.container);
            parent.addChild(this.container);
        }
    }

    /**
     * 获取边界框
     * @returns {Object} 边界框
     */
    getBounds() {
        return this.container.getBounds();
    }

    /**
     * 隐藏阴影层（当碎片被组合时调用）
     */
    hideShadow() {
        if (this.shadowLayer) {
            this.shadowLayer.visible = false;
        }
    }

    /**
     * 显示阴影层
     */
    showShadow() {
        if (this.shadowLayer) {
            this.shadowLayer.visible = true;
        }
    }

    /**
     * 播放吸附成功特效
     * 包含：白色闪光
     * @param {number} delay 延迟时间 (ms)
     */
    playSnapEffect(delay = 0) {
        if (delay > 0) {
            setTimeout(() => this.playSnapEffect(0), delay);
            return;
        }

        // 优化方案：使用与当前 sprite 相同的纹理创建一个覆盖层
        // 这样可以确保形状、位置、缩放完全一致，避免白色方块偏移的问题
        const flash = new PIXI.Sprite(this.sprite.texture);
        
        // 复制变换属性
        flash.position.copyFrom(this.sprite.position);
        flash.scale.copyFrom(this.sprite.scale);
        flash.anchor.copyFrom(this.sprite.anchor);
        flash.rotation = this.sprite.rotation;

        // 设置为纯白高亮
        // 使用 ColorMatrixFilter 将纹理变为纯白
        const colorMatrix = new PIXI.ColorMatrixFilter();
        colorMatrix.brightness(100, false); // 极大提高亮度使其变白
        flash.filters = [colorMatrix];
        
        // 或者简单地使用 tint (如果原图是白色的 mask 构成的)
        // 但对于彩色图片，tint 无法使其变白。
        // 另一种轻量方法：使用 ADD 混合模式配合白色 tint (默认)
        // 叠加一个自身的拷贝，混合模式为 ADD，会让图片瞬间变亮变白
        flash.filters = null; // 重置
        flash.blendMode = 'add';
        flash.alpha = 0.5; // 初始亮度

        this.container.addChild(flash);

        // 闪光动画 - 优化：放慢速度
        let flashAlpha = 0.5;
        const flashTicker = (ticker) => {
            flashAlpha -= 0.02; // 减慢消失速度
            if (flashAlpha <= 0) {
                flash.destroy();
                this.app.ticker.remove(flashTicker);
            } else {
                flash.alpha = flashAlpha;
            }
        };
        this.app.ticker.add(flashTicker);
    }

    /**
     * 销毁
     */
    destroy() {
        this.container.destroy({ children: true });
    }
}

export default Piece;
